import { Button, Input, Modal } from 'antd';
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

import './PasswordModal.scss';

interface PasswordModalProps {
    password: string;
    setPassword: (password: string) => void;
    resetPassword: () => void;
    isVisible: boolean;
    onConfirm: (password: string) => void;
    onCancel: () => void;
}

export const PasswordModal = ({
    password,
    setPassword,
    resetPassword,
    isVisible,
    onConfirm,
    onCancel,
}: PasswordModalProps): JSX.Element => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    const okButtonRef = useRef<HTMLElement>(null);
    const isOKEnabled = useMemo(() => password.trim().length, [password]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            // Enter
            if (isOKEnabled && okButtonRef.current && event.code === 'Enter') {
                okButtonRef.current.click();
            }
        },
        [isOKEnabled]
    );

    const inputRef = useRef(null);

    const focusAndSelectAll = useCallback(() => {
        if (inputRef.current && 'focus' in inputRef.current) {
            (inputRef.current as HTMLInputElement).focus();
            (inputRef.current as HTMLInputElement).select();
        }
    }, []);

    const onCancelClicked = useCallback(() => {
        resetPassword();
        onCancel();
    }, [onCancel, resetPassword]);

    useEffect(() => {
        focusAndSelectAll();
    }, [focusAndSelectAll]);

    return (
        <Modal
            centered
            destroyOnClose
            open={isVisible}
            closable
            footer={null}
            className="PasswordModal"
            onCancel={onCancelClicked}
            title={<div className="PasswordModal__title">{t('SCROLLABLE_AREA.PASSWORD.TITLE')}</div>}
            keyboard
        >
            <div className="PasswordModal__content">
                <Input.Password
                    type={showPassword ? 'text' : 'password'}
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                    onKeyDown={handleKeyDown}
                    ref={inputRef}
                    placeholder="enter password"
                    iconRender={(visible) => (visible ? <EyeInvisibleOutlined /> : <EyeTwoTone />)}
                    visibilityToggle={{ visible: showPassword, onVisibleChange: setShowPassword }}
                />
                <Button.Group className="PasswordModal__buttonGroup">
                    <Button
                        className="PasswordModal__button"
                        ref={okButtonRef}
                        disabled={!isOKEnabled}
                        onClick={() => {
                            focusAndSelectAll();
                            onConfirm(password);
                        }}
                    >
                        {t('SCROLLABLE_AREA.PASSWORD.OK')}
                    </Button>
                    <Button className="PasswordModal__button" onClick={onCancelClicked}>
                        {t('SCROLLABLE_AREA.PASSWORD.CANCEL')}
                    </Button>
                </Button.Group>
            </div>
        </Modal>
    );
};
